import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges, HostListener } from '@angular/core';

import { MaterialCategory } from '@app/models';

export interface FiltersPanelResult {
  selectedCategories: string[];
  selectedSubCategory: string;
}

@Component({
  selector: 'filters-panel',
  templateUrl: './filters-panel.component.html',
  styleUrl: './filters-panel.component.scss',
  standalone: false
})
export class FiltersPanelComponent implements OnChanges {
  @Input() isOpen = false;
  @Input() materialCategories: MaterialCategory[] = [];
  @Input() initialSelectedCategories: string[] = [];
  @Input() initialSelectedSubCategory: string = '';

  @Output() applied = new EventEmitter<FiltersPanelResult>();
  @Output() cleared = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  draftSelectedCategories: Set<string> = new Set();
  draftSelectedSubCategory: string = '';

  ngOnChanges(changes: SimpleChanges) {
    if (changes['isOpen'] && this.isOpen) {
      this.draftSelectedCategories = new Set(this.initialSelectedCategories ?? []);
      this.draftSelectedSubCategory = this.initialSelectedSubCategory ?? '';
    }
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    if (this.isOpen) this.onClose();
  }

  get availableSubCategories(): { categoryName: string; name: string }[] {
    if (!this.materialCategories?.length) return [];
    const out: { categoryName: string; name: string }[] = [];
    for (const cat of this.materialCategories) {
      if (!this.draftSelectedCategories.has(cat?.name)) continue;
      const subs = cat?.subCategories;
      if (!subs?.length) continue;
      for (const sub of subs) {
        if (sub?.name) out.push({ categoryName: cat.name, name: sub.name });
      }
    }
    return out;
  }

  isCategoryChecked(name: string): boolean {
    return this.draftSelectedCategories.has(name);
  }

  toggleCategory(name: string) {
    if (!name) return;
    if (this.draftSelectedCategories.has(name)) {
      this.draftSelectedCategories.delete(name);
      // If sub-category belongs to a deselected category, clear it.
      const ownedSub = this.availableSubCategories.find(s => s.name === this.draftSelectedSubCategory);
      if (!ownedSub) this.draftSelectedSubCategory = '';
    } else {
      this.draftSelectedCategories.add(name);
    }
    // Trigger a fresh Set reference so ngOnChanges-style consumers re-evaluate.
    this.draftSelectedCategories = new Set(this.draftSelectedCategories);
  }

  onSubCategoryChange(value: string) {
    this.draftSelectedSubCategory = value ?? '';
  }

  onApply() {
    this.applied.emit({
      selectedCategories: Array.from(this.draftSelectedCategories),
      selectedSubCategory: this.draftSelectedSubCategory
    });
  }

  onClear() {
    this.draftSelectedCategories = new Set();
    this.draftSelectedSubCategory = '';
    this.cleared.emit();
  }

  onClose() {
    this.closed.emit();
  }

  onBackdropClick() {
    this.onClose();
  }
}
